import {pick} from '@benchling/js-utils';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {NucleotideType} from 'biojs/BioConstants';
import ModelTypeConstants, {
  CUSTOM_TAGGABLE_FOLDER_ITEMS,
  SearchableExclusiveSSIType,
  SequenceSubtype,
} from 'constants/ModelTypeConstants';
import * as RegistryConstants from 'constants/RegistryConstants';
import {
  isTagSchemaSupportedFolderItemType,
  OligoAwareFolderItemType,
  SequenceSchemaType,
  TagSchema,
  TagSchemaSummary,
  TagSchemaSupportedFolderItemType,
} from 'constants/RegistryConstants';
import {FileSummary, isSchemaTag, SchemaTag, Tag} from 'folder-items/FileConstants';
import {
  TagSchemaSequenceSchemaType,
  TagSchemaSupportedFolderItemType as TagSchemaSupportedFolderItemTypeGQL,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import isGraphQL from 'graphql-client/isGraphQL';
import * as BioentityActions from 'inventory/BioentityActions';
import {
  ComputedFieldType,
  FIELD_TYPES,
  FIELD_TYPES_BY_TYPE_STRING,
  FieldType,
  LeafFieldOption,
  LINK_FIELD_TYPES,
  RequiredLinkFolderItemType,
  RequiredLinkTagSchema,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import * as TagUtil from 'schemas/tags/TagUtil';
import ConfirmSetTagSchemaModal from 'tag-schema/ConfirmSetTagSchemaModal';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';
import {assertNever} from 'util/Types';

import {TagSchemaListingGraphQL} from '../../../graphql-fragments/schemas/__generated/TagSchemaListingGraphQL';
import {TagSchemaListingWithFieldsGraphQL} from '../../../graphql-fragments/schemas/__generated/TagSchemaListingWithFieldsGraphQL';
import {TagSchemaSummaryGraphQL} from '../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';
import {toModelTypeConstant} from './ModelTypeConversions';

const {RESIDUES_SUPPORTED_OLIGO_AWARE_TYPES} = RegistryConstants;

export function getFieldTypeName(fieldType: string): string {
  return FIELD_TYPES_BY_TYPE_STRING[fieldType].label.toLowerCase();
}

// Subtype that supports either a graphQL or REST representation of the fields on a schema that are needed
// to determine its associated model type. Not just  Pick<> expression because the enums for the GraphQL and
// REST types are different, so get type conflicts if we don't sepcify the union.
export interface SchemaIdAndTypeInfo
  extends Pick<TagSchemaSummary, 'id' | 'shouldCreateAsOligo' | 'sequenceType'> {
  folderItemType: TagSchemaSupportedFolderItemType | TagSchemaSupportedFolderItemTypeGQL;
}

export function isTagSchemaSet(files: Array<FileSummary>): boolean {
  return files.every((file) => Boolean(file.tagSchema));
}

export function isLinkFieldType(fieldType: FieldType): boolean {
  return LINK_FIELD_TYPES.includes(fieldType);
}

export function isLinkField(tagSchemaField: SchemaField): boolean {
  return isLinkFieldType(tagSchemaField.fieldType);
}

export function isLinkFieldOption(fieldOption: LeafFieldOption): boolean {
  if (fieldOption.fieldDefinition === ComputedFieldType.SNAPSHOT) {
    return false;
  }
  return isLinkFieldType(fieldOption.fieldDefinition);
}

export function isBioentityLinkFieldType(fieldType: FieldType): boolean {
  const linkTypes = [
    FIELD_TYPES.AA_PART_LINK.type,
    FIELD_TYPES.BIOENTITY_LINK.type,
    FIELD_TYPES.PART_LINK.type,
    FIELD_TYPES.TRANSLATION_LINK.type,
    FIELD_TYPES.TRANSCRIPTION_LINK.type,
  ];
  return linkTypes.includes(fieldType);
}

export function isItemLinkFieldType(fieldType: FieldType): boolean {
  return (
    isBioentityLinkFieldType(fieldType) ||
    fieldType === FIELD_TYPES.BATCH_LINK.type ||
    fieldType === FIELD_TYPES.STORABLE_LINK.type ||
    fieldType === FIELD_TYPES.ENTRY_LINK.type
  );
}

export function isStorableLinkFieldType(fieldType: FieldType): boolean {
  return fieldType === FIELD_TYPES.STORABLE_LINK.type;
}

export function entityTypeSupportsResidues(entityType: string | undefined): boolean {
  return entityType != null && (RESIDUES_SUPPORTED_OLIGO_AWARE_TYPES as Array<string>).includes(entityType);
}

export function entityTypeSupportsChemicalStructure(entityType: string | undefined): boolean {
  return entityType === ModelTypeConstants.MOLECULE;
}

/**
 * Return the FolderItem type that corresponds to schemas that can be associated with the file identified
 * by the `fileId` apiIdentifier.
 *
 * Note: this should be kept in sync with HasTagSchemaAndCustomTagsMixin.get_tag_schema_folder_item_type / its
 *   overrides.
 *
 * @param {string} fileId
 * @return {string} - the corresponding FolderItem type
 */
export function schemaTagFolderTypeForFileId(fileId: string): string {
  const fileType = APIUtil.apiIdToType(fileId);
  switch (fileType) {
    case ModelTypeConstants.TEMPLATE:
    case ModelTypeConstants.STAGE_CONFIGURATION:
    case ModelTypeConstants.STAGE_ENTRY:
      return ModelTypeConstants.ENTRY;
    default:
      return fileType;
  }
}

export function hasCustomTagSupport(fileId: string): boolean {
  const itemType = schemaTagFolderTypeForFileId(fileId);
  return (CUSTOM_TAGGABLE_FOLDER_ITEMS as Array<string>).includes(itemType);
}

export function oligoAwareFolderItemTypeForFileType(
  fileType: string,
  sequenceSubtype?: SequenceSubtype
): OligoAwareFolderItemType {
  const folderItemType = fileType;
  if (folderItemType === ModelTypeConstants.SEQUENCE && sequenceSubtype) {
    return sequenceSubtype;
  } else {
    return folderItemType as OligoAwareFolderItemType;
  }
}

export function hasFileSchemaSupport(fileId: string): boolean {
  return isTagSchemaSupportedFolderItemType(schemaTagFolderTypeForFileId(fileId));
}

export function canRemoveTagSchema(fileType: string, isFileRegistered: boolean): boolean {
  // Keep in sync with `files/bulk-set-tag-schema` endpoint
  // Note: Even though the endpoint blocks un-setting the schema if containers already exist, we don't
  // apply that logic here because (a) There's no good way to signal the reason for it to the user, and
  // (b) We would have to fetch data on whether containers exist in the tag schema selector component.
  // Since this is not an expensive user action, it's not a big deal for the feedback loop in this case to be
  // very slightly longer.
  return (
    !(
      isFileRegistered ||
      ([ModelTypeConstants.BASIC_FOLDER_ITEM, ModelTypeConstants.MIXTURE] as Array<string>).includes(fileType)
    ) && !(BenchlingConfig.ELN_ENABLE_SCHEMA_REQUIRED_FOR_REVIEW && ModelTypeConstants.ENTRY === fileType)
  );
}

/**
 * Sets tag schema on files (entities, entries).
 * Opens a confirmation modal if any fields can be converted to custom fields, otherwise
 * calls the action directly.
 */
export function bulkSetTagSchema(
  fileDatas: Array<{id: string; tags: Array<Tag>}>,
  schema: TagSchema | null
): void {
  function onSubmit({deleteOldSchemaTags}: {deleteOldSchemaTags: boolean}): void {
    const schemaId = schema ? schema.id : null;
    // eslint-disable-next-line no-restricted-syntax
    BioentityActions.bulkUpdateTagSchema(_.pluck(fileDatas, 'id'), schemaId, deleteOldSchemaTags);
  }
  const newFieldNames = schema ? schema.fields.map((field) => field.name) : [];
  const convertableSchemaTags: Array<SchemaTag> = fileDatas
    .map(
      (file) =>
        file.tags.filter(
          (tag) => isSchemaTag(tag) && TagUtil.tagHasValue(tag) && !newFieldNames.includes(tag.name)
        ) as Array<SchemaTag>
    )
    .flat();
  const mayDropConvertedFromLinks = convertableSchemaTags.some(
    (schemaTag) => schemaTag.tagSchemaField.isConvertedFromLink
  );

  const convertableFieldNames = convertableSchemaTags.map((schemaTag) => schemaTag.name);
  const forMixtures = fileDatas.some(({id}) => APIUtil.apiIdToType(id) === ModelTypeConstants.MIXTURE);
  if (convertableFieldNames.length || forMixtures) {
    ConfirmSetTagSchemaModal.open({
      convertableFieldNames,
      onSubmit,
      forMixtures,
      mayDropConvertedFromLinks,
    });
  } else {
    onSubmit({deleteOldSchemaTags: false});
  }
}

export function canEditTags(fileId: string, permissions: Permissions): boolean {
  if (APIUtil.isEntry(fileId) || APIUtil.isStageEntry(fileId)) {
    return Boolean(permissions.tagsEditable);
  }
  return permissions.writable;
}

export interface TagSchemaSummaryGraphQLWithExclusiveSSI extends TagSchemaSummaryGraphQL {
  exclusiveSSITypeOverride: SearchableExclusiveSSIType | null;
}

export function getTagSchemaOligoAwareFolderItemType(
  tagSchema:
    | TagSchemaSummary
    | TagSchemaSummaryGraphQL
    | TagSchemaListingGraphQL
    | TagSchemaSummaryGraphQLWithExclusiveSSI
): OligoAwareFolderItemType {
  if (tagSchema.folderItemType === ModelTypeConstants.BASIC_FOLDER_ITEM) {
    if (tagSchema.exclusiveSSITypeOverride) {
      return tagSchema.exclusiveSSITypeOverride;
    }
  }
  // Now that Exclusive SSI override is done.
  if (tagSchema.folderItemType === ModelTypeConstants.SEQUENCE) {
    if (isRNASequenceSchema(tagSchema)) {
      return ModelTypeConstants.RNA_SEQUENCE;
    } else if (isDNAOligoSchema(tagSchema)) {
      return ModelTypeConstants.DNA_OLIGO;
    }

    if (isRNAOligoSchema(tagSchema)) {
      return ModelTypeConstants.RNA_OLIGO;
    } else {
      return ModelTypeConstants.SEQUENCE;
    }
  } else {
    return isGraphQL(tagSchema)
      ? (toModelTypeConstant(tagSchema.folderItemType) as OligoAwareFolderItemType)
      : tagSchema.folderItemType;
  }
}

/**
 * While a tagSchema is technically associated with a single folderItemType, there are a couple of exceptional
 * cases where we need to accept additional types. This behavior should be considered tech debt.
 *
 * Use getTagSchemaOligoAwareFolderItemType to get the *default* OligoAwareFolderItemType for a tag schema.
 */
export function tagSchemaToAllOligoAwareFolderItemTypes(tagSchema: TagSchemaSummary): Array<string> {
  if (tagSchema.folderItemType === ModelTypeConstants.ENTRY) {
    return [ModelTypeConstants.ENTRY, ModelTypeConstants.STAGE_ENTRY];
  } else if (tagSchema.sequenceType === TagSchemaSequenceSchemaType.MIXED) {
    return [ModelTypeConstants.DNA_OLIGO, ModelTypeConstants.SEQUENCE];
  } else {
    return [getTagSchemaOligoAwareFolderItemType(tagSchema)];
  }
}

/**
 * For sequence schemas, determine whether the entity type should be treated as DNA oligo rather than
 * (long) DNA sequence, (long) RNA sequence, or RNA oligo. This is used for menus that present schemas
 * organized by type, so it's important that no schema has multiple types in this sense.
 *
 * DNA-only and oligo-only schemas are straightforward. For mixed schemas, we use the
 * "shouldCreateAsOligo" flag as a heuristic for how to categorize it. Eventually, we will convert
 * all mixed schemas to specific schemas, so this check will become simpler.
 */
export function isDNAOligoSchema(schema: SchemaIdAndTypeInfo): boolean {
  if (schema.folderItemType !== ModelTypeConstants.SEQUENCE) {
    return false;
  }
  if (schema.sequenceType === SequenceSchemaType.OLIGO) {
    return true;
  } else if (
    [SequenceSchemaType.DNA_SEQUENCE, SequenceSchemaType.RNA_SEQUENCE, SequenceSchemaType.RNA_OLIGO].includes(
      schema.sequenceType!
    )
  ) {
    return false;
  } else {
    return schema.shouldCreateAsOligo;
  }
}

export function isDNAOligoSchemaGraphQL(schema: TagSchemaListingGraphQL): boolean {
  if (toModelTypeConstant(schema.folderItemType) !== ModelTypeConstants.SEQUENCE) {
    return false;
  }
  if (schema.sequenceType === SequenceSchemaType.OLIGO) {
    return true;
  } else if (
    [SequenceSchemaType.DNA_SEQUENCE, SequenceSchemaType.RNA_SEQUENCE, SequenceSchemaType.RNA_OLIGO].includes(
      schema.sequenceType!
    )
  ) {
    return false;
  } else {
    return schema.shouldCreateAsOligo;
  }
}

export function isRNAOligoSchema(schema: SchemaIdAndTypeInfo): boolean {
  return (
    schema.folderItemType === ModelTypeConstants.SEQUENCE &&
    schema.sequenceType === SequenceSchemaType.RNA_OLIGO
  );
}

export function isRNAOligoSchemaGraphQL(schema: TagSchemaListingGraphQL): boolean {
  return (
    toModelTypeConstant(schema.folderItemType) === ModelTypeConstants.SEQUENCE &&
    schema.sequenceType === SequenceSchemaType.RNA_OLIGO
  );
}

/**
 * Determine if the schema field can accept oligos and return the eligible oligo types by nucleotide type.
 * Fields can be compatible with DNA Oligos, RNA Oligos, both, or neither. Must be non-computed, non-archived,
 * and must either have an oligo-specific required schema, or be a generic sequence field.
 * Once we have generic oligo fields, generic sequence fields won't be allowed here.
 */
export function getFieldEligibleOligoNucleotideTypes(field: SchemaField): Set<NucleotideType> {
  if (field.isComputed || field.archiveRecord != null || field.fieldType !== FieldType.BIOENTITY_LINK) {
    return new Set([]);
  }

  const requiredLinkSequenceType = (field.requiredLink as RequiredLinkTagSchema | null)?.tagSchema
    ?.sequenceType;
  const folderItemType = (field.requiredLink as RequiredLinkFolderItemType | null)?.folderItemType;

  switch (requiredLinkSequenceType) {
    case TagSchemaSequenceSchemaType.OLIGO:
    case TagSchemaSequenceSchemaType.MIXED:
      return new Set([NucleotideType.DNA]);
    case TagSchemaSequenceSchemaType.RNA_OLIGO:
      return new Set([NucleotideType.RNA]);
    default:
      return folderItemType === 'sequence' ? new Set([NucleotideType.DNA, NucleotideType.RNA]) : new Set([]);
  }
}

/**
 * Determine if an entity created of this schema would be of type DNA sequence (as opposed to RNA sequence or
 * DNA or RNA oligo).
 */
export function isDNASequenceSchema(schema: SchemaIdAndTypeInfo): boolean {
  return (
    schema.folderItemType === ModelTypeConstants.SEQUENCE &&
    (schema.sequenceType === SequenceSchemaType.DNA_SEQUENCE ||
      schema.sequenceType === SequenceSchemaType.MIXED) &&
    !isDNAOligoSchema(schema) &&
    !isRNAOligoSchema(schema)
  );
}

export function isDNASequenceSchemaGraphQL(schema: TagSchemaListingGraphQL): boolean {
  return (
    toModelTypeConstant(schema.folderItemType) === ModelTypeConstants.SEQUENCE &&
    (schema.sequenceType === SequenceSchemaType.DNA_SEQUENCE ||
      schema.sequenceType === SequenceSchemaType.MIXED) &&
    !isDNAOligoSchemaGraphQL(schema) &&
    !isRNAOligoSchemaGraphQL(schema)
  );
}

/**
 * Determine if an entity created of this schema would be of type RNA sequence (as opposed to DNA sequence or
 * DNA or RNA oligo).
 */
export function isRNASequenceSchema(schema: SchemaIdAndTypeInfo): boolean {
  return (
    schema.folderItemType === ModelTypeConstants.SEQUENCE &&
    schema.sequenceType === SequenceSchemaType.RNA_SEQUENCE &&
    !isDNAOligoSchema(schema) &&
    !isRNAOligoSchema(schema)
  );
}

export function isRNASequenceSchemaGraphQL(schema: TagSchemaListingGraphQL): boolean {
  return (
    toModelTypeConstant(schema.folderItemType) === ModelTypeConstants.SEQUENCE &&
    schema.sequenceType === SequenceSchemaType.RNA_SEQUENCE &&
    !isDNAOligoSchemaGraphQL(schema) &&
    !isRNAOligoSchemaGraphQL(schema)
  );
}

export function oligoAwareFolderItemTypeMatchesTagSchemaType(
  itemType: OligoAwareFolderItemType,
  folderItemType: string,
  sequenceType: SequenceSchemaType | null
): boolean {
  if (itemType === ModelTypeConstants.SEQUENCE) {
    return (
      folderItemType === ModelTypeConstants.SEQUENCE &&
      [SequenceSchemaType.DNA_SEQUENCE, SequenceSchemaType.MIXED].includes(sequenceType!)
    );
  } else if (itemType === ModelTypeConstants.RNA_SEQUENCE) {
    return folderItemType === ModelTypeConstants.SEQUENCE && sequenceType === SequenceSchemaType.RNA_SEQUENCE;
  } else if (itemType === ModelTypeConstants.DNA_OLIGO) {
    return (
      folderItemType === ModelTypeConstants.SEQUENCE &&
      [SequenceSchemaType.OLIGO, SequenceSchemaType.MIXED].includes(sequenceType!)
    );
  } else if (itemType === ModelTypeConstants.RNA_OLIGO) {
    return folderItemType === ModelTypeConstants.SEQUENCE && sequenceType === SequenceSchemaType.RNA_OLIGO;
  } else {
    return folderItemType === itemType;
  }
}

/**
 * Determine if this tag schema is eligible for an entity of this type.
 */
export function oligoAwareFolderItemTypeMatchesTagSchema(
  itemType: OligoAwareFolderItemType,
  tagSchema: SchemaIdAndTypeInfo
): boolean {
  return oligoAwareFolderItemTypeMatchesTagSchemaType(
    itemType,
    tagSchema.folderItemType,
    tagSchema.sequenceType
  );
}

export function getTagSchemaTypeName({
  schema,
  folderItemType,
}: {
  schema?: Pick<TagSchema, 'folderItemType' | 'sequenceType'>;
  folderItemType?: string;
}): string {
  assert(
    (schema !== undefined || folderItemType !== undefined) &&
      !(schema !== undefined && folderItemType !== undefined),
    'One of schema or folderItemType must be provided'
  );
  const itemType = schema?.folderItemType ?? folderItemType;
  switch (itemType) {
    case ModelTypeConstants.SEQUENCE: {
      if (!schema) {
        return 'DNA Sequence';
      }
      const sequenceType = schema.sequenceType!;
      switch (sequenceType) {
        case SequenceSchemaType.DNA_SEQUENCE:
          return 'DNA Sequence';
        case SequenceSchemaType.RNA_SEQUENCE:
          return 'RNA Sequence';
        case SequenceSchemaType.OLIGO:
          return 'DNA Oligo';
        case SequenceSchemaType.RNA_OLIGO:
          return 'RNA Oligo';
        case SequenceSchemaType.MIXED:
          return 'Sequence (DNA or Oligo)';
        default:
          assertAndContinue(false, `Unknown sequence schema type: ${assertNever(sequenceType)}`, {
            sequenceType,
            schema,
          });
          return '(unknown)';
      }
    }
    case ModelTypeConstants.BASIC_FOLDER_ITEM:
      return 'Custom Entity';
    case ModelTypeConstants.PROTEIN:
      return 'AA Sequence';
    case ModelTypeConstants.MOLECULE:
      return 'Molecule';
    case ModelTypeConstants.MIXTURE:
      return 'Mixture';
    case ModelTypeConstants.ENTRY:
      return 'Entry';
    default:
      assertAndContinue(false, `Unknown schema type: ${itemType}`, {itemType, schema});
      return '(unknown)';
  }
}

export function getTagSchemaSummaryFromListingWithFields(
  tagSchema: TagSchemaListingWithFieldsGraphQL
): TagSchemaSummary {
  return {
    id: tagSchema.id,
    name: tagSchema.name,
    folderItemType: toModelTypeConstant(tagSchema.folderItemType) as TagSchemaSupportedFolderItemType,
    sequenceType: tagSchema.sequenceType!,
    shouldCreateAsOligo: tagSchema.shouldCreateAsOligo,
    iconId: tagSchema.iconId as BenchlingIconName,
    sqlIdentifier: tagSchema.sqlIdentifier,
    archiveRecord: tagSchema.archiveRecord,
  };
}

export function getTagSchemaSummaryGraphQLFromListingWithFields(
  tagSchema: TagSchemaListingWithFieldsGraphQL
): TagSchemaSummaryGraphQL {
  return pick(
    tagSchema,
    '__typename',
    'id',
    'name',
    'folderItemType',
    'sequenceType',
    'shouldCreateAsOligo',
    'iconId',
    'sqlIdentifier',
    'archiveRecord'
  );
}
